import { useEffect, useState } from 'react'
import { connect } from 'react-redux'
import { OptionsHOC, TrackingHOC } from 'hocs'
import { updateTitle } from '../Breadcrumbs/actions'
import OsearchComponent from 'components/Osearch'
import Filters from 'components/Osearch/Filters'
import Results from 'components/Osearch/Results'
import { fetchSearch } from './actions'
import { getUrl, getTitle, getDescription } from './helpers'
import FiltersBreadCrumbs from 'components/Osearch/FiltersBreadCrumbs'

const Osearch = ({
  location,
  updateTitle,
  createEvent,
  getOption,
  fetchSearch,
  osearch
}) => {
  const searchQuery = location?.query?.q
  const [isAllowToFilter, setIsAllowToFilter] = useState(true)
  const [results, setResults] = useState([])
  const [isScrolledDown, setIsScrolledDown] = useState(false)
  const [filterCrumbs, setFilterCrumbs] = useState([])
  const [selectedPhoneFilter, setSelectedPhoneFilter] = useState('')
  const [selectedManufacturerFilter, setSelectedManufacturerFilter] =
    useState('')
  const [selectedModelFilter, setSelectedModelFilter] = useState('')

  const [pageDetails, setPageDetails] = useState({
    pageNumber: 0,
    pageLength: 10
  })
  const [filterOptions, setFilterOptions] = useState({
    phoneTypeOptions: [],
    manufacturerOptions: [],
    modelOptions: []
  })
  const [filters, setFilters] = useState({})

  const [prevQuery, setPrevQuery] = useState()
  const [prevFilters, setPrevFilters] = useState()

  const getMappedResults = () => {
    const { searchResults } = osearch ?? {}
    const resultsArr = searchResults?.results ?? []
    if (!resultsArr) return []
    return resultsArr.map((result, index) => {
      const src = result._source || {}
      return {
        url: getUrl(src.type, src.pk || src.PK, src.phonefk),
        type: src.type,
        title: getTitle(src),
        description: getDescription(src),
        phonefk: src.phonefk
      }
    })
  }

  useEffect(() => {
    setFilterCrumbs(() =>
      filters
        ? Object.keys(filters).map(key => ({
            id: `${key}-${filters[key]}`,
            label: filters[key],
            type: key
          }))
        : []
    )
    Object.keys(filters || {}).forEach(key => {
      if (key === 'phone_type') {
        setSelectedPhoneFilter(filters[key])
      }
      if (key === 'manufacturer') {
        setSelectedManufacturerFilter(filters[key])
      }
      if (key === 'model') {
        setSelectedModelFilter(filters[key])
      }
    })
  }, [filters])

  useEffect(() => {
    let hasReachedBottom = false
    const handleScroll = () => {
      const scrollY = window.scrollY || window.pageYOffset
      const viewportHeight = window.innerHeight
      const fullHeight = document.documentElement.scrollHeight
      if (scrollY + viewportHeight >= fullHeight - 100) {
        if (!hasReachedBottom) {
          hasReachedBottom = true
          setIsScrolledDown(true)
        }
      } else {
        hasReachedBottom = false
        setIsScrolledDown(false)
      }
    }
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  useEffect(() => {
    const { searchResults } = osearch || {}
    if (isAllowToFilter) {
      setFilterOptions({
        phoneTypeOptions: searchResults?.aggs?.phone_type ?? [],
        manufacturerOptions: searchResults?.aggs?.manufacturer ?? [],
        modelOptions: searchResults?.aggs?.model ?? []
      })
      setIsAllowToFilter(false)
    }

    const mappedResults = getMappedResults()
    // If searchQuery or filters changed, reset results
    if (
      searchQuery !== prevQuery ||
      JSON.stringify(filters) !== JSON.stringify(prevFilters)
    ) {
      setResults(mappedResults)
      setPrevQuery(searchQuery)
      setPrevFilters(filters)
    } else {
      // If pageDetails.pageNumber > 0, append
      if (pageDetails.pageNumber > 0 && mappedResults.length > 0) {
        setResults(prev => [...prev, ...mappedResults])
      } else {
        setResults(mappedResults)
      }
    }
  }, [osearch?.searchResults])

  useEffect(() => {
    fetchSearch(
      searchQuery,
      filters,
      pageDetails.pageNumber,
      pageDetails.pageLength
    )
  }, [filters, pageDetails.pageNumber, pageDetails.pageLength, searchQuery])

  useEffect(() => {
    updateTitle(searchQuery)
    setIsAllowToFilter(true)
    setPageDetails({
      pageNumber: 0,
      pageLength: 10
    })
    setFilterOptions({
      phoneTypeOptions: [],
      manufacturerOptions: [],
      modelOptions: []
    })
    fetchSearch(searchQuery, {}, 0, 10)
  }, [searchQuery])

  useEffect(() => {
    if (!isScrolledDown) return

    const total = osearch?.searchResults?.total || 0
    const defaultPageLength = 10
    const nextPageNumber = pageDetails.pageNumber + 1
    const maxPageNumber = Math.ceil(total / defaultPageLength)
    if (nextPageNumber < maxPageNumber) {
      // For last page, adjust pageLength to remaining
      let nextPageLength = defaultPageLength
      if (nextPageNumber === maxPageNumber - 1) {
        nextPageLength = total - nextPageNumber * defaultPageLength
      }
      setPageDetails({
        pageNumber: nextPageNumber,
        pageLength: nextPageLength
      })
    }
  }, [isScrolledDown])

  const trackClick = item => {
    const { url } = item ?? {}
    createEvent({
      category: 'search',
      data: { action: 'success', search_term: searchQuery, link_to: url }
    })
  }

  const handleRemoveCrumb = crumb => {
    const { id, type } = crumb ?? {}
    setFilterCrumbs(prev => prev.filter(c => c.id !== id))
    if (type === 'phone_type') {
      setSelectedPhoneFilter(null)
    }
    if (type === 'manufacturer') {
      setSelectedManufacturerFilter(null)
    }
    if (type === 'model') {
      setSelectedModelFilter(null)
    }
  }

  return (
    <OsearchComponent
      phoneTypeOptions={getOption('search.phoneTypeOptions', [])}
      searchQuery={searchQuery}
      filters={
        <Filters
          phoneTypeOptions={filterOptions.phoneTypeOptions}
          manufacturerOptions={filterOptions.manufacturerOptions}
          modelOptions={filterOptions.modelOptions}
          selectedPhoneFilter={selectedPhoneFilter}
          selectedManufacturerFilter={selectedManufacturerFilter}
          selectedModelFilter={selectedModelFilter}
          getOption={getOption}
          onChangeFilters={setFilters}
        />
      }
      results={
        <>
          <FiltersBreadCrumbs
            crumbs={filterCrumbs}
            onRemove={handleRemoveCrumb}
          />
          <Results
            results={results}
            searchQuery={searchQuery}
            isLoading={osearch?.isSearching}
            onClick={trackClick}
          />
        </>
      }
    />
  )
}

const mapStateToProps = state => ({
  ...state
})

const mapDispatchToProps = dispatch => ({
  updateTitle: title => dispatch(updateTitle(title)),
  fetchSearch: (query, filters, pageNumber, pageLength) =>
    dispatch(fetchSearch.request(query, filters, pageNumber, pageLength))
})

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(TrackingHOC(OptionsHOC(Osearch)))
